import type { OTPInputType } from "./types";

export function sanitizeInput(value: string, inputType: OTPInputType): string {
  if (inputType === "numeric") return value.replace(/\D/g, "");
  return value.replace(/[^a-zA-Z0-9]/g, "");
}

export function normalizeValue(
  value: string | undefined,
  length: number,
  inputType: OTPInputType,
): string {
  return sanitizeInput(value ?? "", inputType).slice(0, length);
}

export function toDigitArray(value: string, length: number): string[] {
  return Array.from({ length }, (_, index) => value[index] ?? "");
}

export function replaceAt(value: string[], index: number, nextChar: string): string[] {
  const next = [...value];
  next[index] = nextChar;
  return next;
}

/**
 * The extra character when a box that held `current` now holds `current` plus
 * one character, on either side: what a keystroke at a caret leaves. Null for
 * any other text, and for an empty box.
 */
export function keystrokeInto(current: string, text: string): string | null {
  if (!current || text.length !== 2) return null;
  if (text[0] === current) return text[1] ?? null;
  if (text[1] === current) return text[0] ?? null;
  return null;
}

/**
 * The whole code in a box's new text, already filtered, or null if it isn't
 * one. A native paste or autofill into a box that held a digit (`current`)
 * arrives with that digit at one end, so it's dropped. When the digit is at
 * both ends, the code could be either reading: `ambiguous`, so nothing is
 * guessed. Text past `length` is cut.
 */
export function wholeCode(
  current: string,
  text: string,
  length: number,
): { code: string } | { ambiguous: true } | null {
  if (text.length === length) return { code: text };
  if (current && text.length === length + 1) {
    const after = text.startsWith(current);
    const before = text.endsWith(current);
    if (after && before) return { ambiguous: true };
    if (after) return { code: text.slice(1) };
    if (before) return { code: text.slice(0, -1) };
  }
  if (text.length > length) return { code: text.slice(0, length) };
  return null;
}

/** Inserts `char` at `index`; later digits shift right and what overflows `length` drops. */
export function insertAt(value: string[], index: number, char: string, length: number): string[] {
  const joined = joinDigits(value);
  return toDigitArray(
    (joined.slice(0, index) + char + joined.slice(index)).slice(0, length),
    length,
  );
}

export function fillForward(
  value: string[],
  startIndex: number,
  chars: string,
  maxLength: number,
): { next: string[]; nextFocusIndex: number } {
  const next = [...value];
  let cursor = startIndex;

  for (const char of chars) {
    if (cursor >= maxLength) break;
    next[cursor] = char;
    cursor += 1;
  }

  return { next, nextFocusIndex: Math.min(cursor, maxLength - 1) };
}

export function joinDigits(digits: string[]): string {
  return digits.join("");
}

export function isComplete(value: string, length: number): boolean {
  return value.length === length;
}
